import { cva, type VariantProps } from "class-variance-authority";
import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 rounded-xl font-medium transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400 focus-visible:ring-offset-2 focus-visible:ring-offset-secondary-950 disabled:opacity-50 disabled:pointer-events-none select-none",
  {
    variants: {
      variant: {
        primary:
          "bg-primary-600 text-white hover:bg-primary-500 hover:shadow-lg hover:shadow-primary-500/20 active:scale-[0.98]",
        outline:
          "border border-primary-500/45 bg-primary-500/5 text-secondary-300 hover:text-white hover:border-primary-400/60 hover:bg-primary-500/10 active:scale-[0.98]",
        subtle:
          "bg-secondary-800/60 border border-secondary-700/60 text-secondary-300 hover:text-white hover:bg-secondary-700/60 hover:border-primary-500/40 active:scale-[0.98]",
      },
      size: {
        sm: "min-h-[40px] px-4 py-2 text-sm",
        md: "min-h-[44px] px-6 py-2.5 text-sm font-semibold",
        lg: "min-h-[52px] px-8 py-3 text-base font-semibold",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "md",
    },
  }
);

interface ButtonProps
  extends VariantProps<typeof buttonVariants>,
    Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "color"> {
  /** Renders as a react-router Link when provided. */
  to?: string;
  /** Renders as a plain anchor when provided. */
  href?: string;
  /** Opens href in a new tab (only used with href). */
  external?: boolean;
  /** Stretch to fill the parent width. */
  fullWidth?: boolean;
}

export function Button({
  className,
  variant,
  size,
  to,
  href,
  external,
  fullWidth,
  children,
  ...props
}: ButtonProps) {
  const cls = cn(buttonVariants({ variant, size }), fullWidth && "w-full", className);

  if (to) {
    return (
      <Link to={to} className={cls}>
        {children}
      </Link>
    );
  }

  if (href) {
    return (
      <a
        href={href}
        className={cls}
        {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
      >
        {children}
      </a>
    );
  }

  return (
    <button className={cls} {...props}>
      {children}
    </button>
  );
}

export default Button;
